import { Transition } from "@headlessui/react";
import clsx from "clsx";
import { CircleAlert, XIcon } from "lucide-react";
import { Fragment, useCallback, useEffect, useState } from "react";
import { AppText } from "@/components/Text/AppText";

interface AdminErrorNotificationProps {
    title?: string;
    errors: Record<string, any> | string | string[];
    autoHide?: boolean;
    autoHideDelay?: number;
    className?: string;
}

export const AdminErrorNotification = ({
    title = "Validation Errors",
    errors,
    autoHide = true,
    autoHideDelay = 7500,
    className,
}: AdminErrorNotificationProps) => {
    const [shouldShow, setShouldShow] = useState(false);
    const [currErrors, setCurrErrors] = useState(errors);

    const handleClose = useCallback(() => {
        setCurrErrors({});
        setShouldShow(false);
    }, []);

    useEffect(() => {
        setCurrErrors(errors);
        setShouldShow(Object.keys(errors).length > 0);
    }, [errors]);

    useEffect(() => {
        if (shouldShow && autoHide) {
            const timeoutId = setTimeout(handleClose, autoHideDelay);
            return () => clearTimeout(timeoutId);
        }
    }, [shouldShow, autoHide, autoHideDelay, handleClose]);

    // Convert errors to a flat array of error messages
    const getErrorMessages = (): string[] => {
        if (typeof currErrors === "string") {
            return [currErrors];
        }
        if (Array.isArray(currErrors)) {
            return currErrors;
        }
        if (typeof currErrors === "object" && currErrors !== null) {
            const messages: string[] = [];
            const extractErrors = (obj: any, prefix = "") => {
                Object.keys(obj).forEach(key => {
                    const value = obj[key];
                    const fullKey = prefix ? `${prefix}.${key}` : key;

                    if (typeof value === "string") {
                        messages.push(value);
                    } else if (Array.isArray(value)) {
                        value.forEach((item, index) => {
                            if (typeof item === "string") {
                                messages.push(item);
                            } else if (
                                typeof item === "object" &&
                                item !== null
                            ) {
                                extractErrors(item, `${fullKey}[${index}]`);
                            }
                        });
                    } else if (typeof value === "object" && value !== null) {
                        extractErrors(value, fullKey);
                    }
                });
            };
            extractErrors(currErrors);
            return messages;
        }
        return [];
    };

    const errorMessages = getErrorMessages();

    if (!shouldShow || errorMessages.length === 0) {
        return null;
    }

    return (
        <div
            aria-live="assertive"
            className={clsx(
                "pointer-events-none fixed bottom-4 left-4 z-[100000] min-w-[500px] max-w-sm",
                className,
            )}
        >
            <Transition
                show={shouldShow}
                as={Fragment}
                enter="transform ease-out duration-300 transition"
                enterFrom="translate-y-2 opacity-0"
                enterTo="translate-y-0 opacity-100"
                leave="transition ease-in duration-100"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
            >
                <div className="pointer-events-auto w-full overflow-hidden rounded-lg bg-white shadow-lg ring-2 ring-error-500">
                    <div className="p-4">
                        <div className="flex items-start">
                            <div className="flex-shrink-0">
                                <CircleAlert
                                    className="h-6 w-6 text-error-500"
                                    aria-hidden="true"
                                />
                            </div>
                            <div className="ml-3 w-0 flex-1">
                                <AppText variant="contextheading">
                                    {title}
                                </AppText>
                                <div className="mt-2 max-h-32 overflow-y-auto">
                                    {errorMessages.map((message, index) => (
                                        <AppText
                                            key={`error-${index}`}
                                            className="mt-1 text-neutral-700"
                                            variant="regular"
                                        >
                                            • {message}
                                        </AppText>
                                    ))}
                                </div>
                            </div>
                            <div className="ml-4 flex flex-shrink-0">
                                <button
                                    type="button"
                                    className="inline-flex rounded-lg bg-white text-neutral-900 hover:text-neutral-500 focus:outline-none"
                                    onClick={handleClose}
                                >
                                    <span className="sr-only">Close</span>
                                    <XIcon
                                        size={20}
                                        aria-hidden="true"
                                    />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </Transition>
        </div>
    );
};
